import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';

interface AdminStats {
  pendingVerifications: number;
  ordersInEscrow: number;
  totalHeldGBP: number;
  disputes: number;
  gmv30dGBP: number;
  buyers: number;
  sellers: number;
  activeListings: number;
  openComplaints: number;
  suspended: number;
  pendingIdChecks: number;
}

interface AdminOrder {
  id: string;
  reference: string;
  productTitle: string;
  buyerName: string;
  amountGBP: number;
  state: string;
  createdAt: string;
}

/** Order state → label + pill + dot colour, for the table's status chips. */
function orderStatus(state: string): { label: string; pill: string; dot: string } {
  switch (state) {
    case 'RELEASED':      return { label: 'Released',      pill: 'pill-ok',   dot: 'bg-ok' };
    case 'COLLECTED':     return { label: 'Collected',     pill: 'pill-sage', dot: 'bg-sage' };
    case 'IN_LOCKER':     return { label: 'In locker',     pill: 'pill-sage', dot: 'bg-sage' };
    case 'SHIPPED':       return { label: 'Shipped',       pill: 'pill-sage', dot: 'bg-sage' };
    case 'AWAITING_DROP': return { label: 'Awaiting drop', pill: 'pill-warn', dot: 'bg-warn' };
    case 'PAID_HELD':     return { label: 'In escrow',     pill: 'pill-gold', dot: 'bg-gold' };
    case 'DISPUTED':      return { label: 'Disputed',      pill: 'pill-bad',  dot: 'bg-bad' };
    case 'REFUNDED':      return { label: 'Refunded',      pill: 'pill-clay', dot: 'bg-clay' };
    case 'CANCELLED':     return { label: 'Cancelled',     pill: 'bg-cream-deep text-mute', dot: 'bg-mute' };
    default:              return { label: 'Pending',       pill: 'bg-cream-deep text-mute', dot: 'bg-mute' };
  }
}

/** Live pipeline buckets - lifecycle stages, each a reserved status colour + label. */
const PIPELINE = [
  { key: 'escrow',   label: 'In escrow', bar: 'bg-gold', dot: 'bg-gold', states: ['CREATED', 'PAID_HELD', 'AWAITING_DROP'] },
  { key: 'shipping', label: 'Shipping',  bar: 'bg-sage', dot: 'bg-sage', states: ['SHIPPED', 'IN_LOCKER'] },
  { key: 'released', label: 'Released',  bar: 'bg-ok',   dot: 'bg-ok',   states: ['COLLECTED', 'RELEASED'] },
  { key: 'disputed', label: 'Disputed',  bar: 'bg-bad',  dot: 'bg-bad',  states: ['DISPUTED', 'REFUNDED'] },
] as const;

function initials(name: string) {
  const p = name.trim().split(/\s+/).filter(Boolean);
  return ((p[0]?.[0] ?? '') + (p[1]?.[0] ?? '')).toUpperCase() || '·';
}

const ORDER_COLS = 'grid grid-cols-[0.8fr_1.6fr_1.6fr_0.8fr_1fr_auto] gap-3 items-center';

export default function AdminOverview() {
  const { data } = useQuery<AdminStats>({
    queryKey: ['admin', 'overview'],
    queryFn: async () => (await api.get('/admin/overview')).data,
  });
  const { data: orders = [], isLoading: ordersLoading } = useQuery<AdminOrder[]>({
    queryKey: ['admin', 'orders', 'recent'],
    queryFn: async () => (await api.get('/admin/orders')).data,
  });

  const n = (v?: number) => (v === undefined ? '-' : v.toLocaleString());
  const money = (v?: number) => `£${(v ?? 0).toLocaleString()}`;

  // Live order pipeline, computed from the orders we already fetched.
  const pipeline = PIPELINE.map((b) => ({ ...b, count: orders.filter((o) => (b.states as readonly string[]).includes(o.state)).length }));
  const pipelineTotal = pipeline.reduce((s, b) => s + b.count, 0);

  const recent = orders.slice(0, 6);
  // Highlight one row like the reference's selected row - the newest open
  // dispute if there is one, otherwise the most recent order.
  const heroId = (recent.find((o) => o.state === 'DISPUTED') ?? recent[0])?.id;

  return (
    <div className="p-6 md:p-10">
      {/* Signature hero - title + GMV + live order-pipeline meter */}
      <div className="section-dark relative overflow-hidden bg-ink text-cream rounded-bold p-7 md:p-8 shadow-luxe">
        <div className="absolute inset-0 bg-gold-mesh opacity-40" />
        <div className="absolute inset-0 bg-dot-light bg-[length:24px_24px] opacity-25" />
        <div className="relative flex items-start justify-between gap-6 flex-wrap">
          <div className="min-w-0">
            <div className="mono text-[10px] tracking-[0.34em] uppercase text-gold-light/80">Admin · Operations</div>
            <h1 className="display text-4xl md:text-5xl mt-2.5 leading-none">Operations</h1>
            <p className="text-cream/60 text-[13.5px] mt-2.5 max-w-[56ch] leading-relaxed">Everything moving through Souqli - funds, orders, verification and disputes, in one view.</p>
          </div>
          <span className="pill bg-cream/10 text-cream/70 text-[11px] shrink-0">Last 30 days</span>
        </div>
        <div className="relative grid md:grid-cols-[1fr_1.35fr] gap-7 md:gap-9 items-center mt-7 pt-6 border-t border-cream/10">
          <div>
            <div className="mono text-[10px] tracking-[0.28em] uppercase text-gold-light/80">30-day GMV</div>
            <div className="display text-6xl mt-2 leading-[0.95]">{money(data?.gmv30dGBP)}</div>
            <div className="text-cream/55 text-[12.5px] mt-2.5">Gross merchandise value, trailing 30 days</div>
          </div>

          <div className="md:border-l md:border-cream/10 md:pl-9">
            <div className="flex items-baseline justify-between mb-3">
              <span className="mono text-[10px] tracking-[0.24em] uppercase text-cream/55">Order pipeline</span>
              <span className="display text-2xl">{pipelineTotal} <span className="text-cream/50 text-base">live</span></span>
            </div>
            {pipelineTotal > 0 ? (
              <div className="flex gap-[2px] h-3.5 rounded-lg overflow-hidden bg-cream/10">
                {pipeline.filter((b) => b.count > 0).map((b) => (
                  <span key={b.key} className={`block rounded-[3px] ${b.bar}`} style={{ width: `${(b.count / pipelineTotal) * 100}%` }} />
                ))}
              </div>
            ) : (
              <div className="h-3.5 rounded-lg bg-cream/10" />
            )}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-5 gap-y-2.5 mt-4">
              {pipeline.map((b) => (
                <div key={b.key} className="flex items-center gap-2 text-[12px] text-cream/80">
                  <span className={`w-2 h-2 rounded-full ${b.dot}`} />
                  <span>{b.label}</span>
                  <span className="display text-[15px] text-cream ml-auto">{b.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3.5">
        <Tile label="Buyers" value={n(data?.buyers)} to="/control-7f3a9b2c/users" />
        <Tile label="Sellers" value={n(data?.sellers)} to="/control-7f3a9b2c/users" accent="gold" />
        <Tile label="Active listings" value={n(data?.activeListings)} to="/control-7f3a9b2c/listings" accent="sage" sub="live on the marketplace" />
        <Tile label="Held in escrow" value={money(data?.totalHeldGBP)} to="/control-7f3a9b2c/escrow" sub={`${n(data?.ordersInEscrow)} orders in flight`} />
      </div>

      {/* Needs attention */}
      <div className="flex items-baseline justify-between mt-9 mb-3.5">
        <h2 className="display text-2xl">Needs attention</h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3.5">
        <Attn label="Listing queue"   value={n(data?.pendingVerifications)} to="/control-7f3a9b2c/verify" dot="bg-warn" on={(data?.pendingVerifications ?? 0) > 0} />
        <Attn label="ID checks"       value={n(data?.pendingIdChecks)}      to="/control-7f3a9b2c/verify" dot="bg-gold" on={(data?.pendingIdChecks ?? 0) > 0} />
        <Attn label="Open disputes"   value={n(data?.disputes)}            to="/control-7f3a9b2c/escrow" dot="bg-bad"  on={(data?.disputes ?? 0) > 0} />
        <Attn label="Open complaints" value={n(data?.openComplaints)}      to="/control-7f3a9b2c/complaints" dot="bg-clay" on={(data?.openComplaints ?? 0) > 0} />
      </div>

      {/* Latest orders */}
      <div className="flex items-end justify-between mt-9 mb-3.5">
        <h2 className="display text-2xl">Latest orders</h2>
        <Link to="/control-7f3a9b2c/orders" className="text-[12px] text-gold-dark hover:underline underline-offset-2">View all orders →</Link>
      </div>

      <div className="card overflow-x-auto">
        <div className={`${ORDER_COLS} px-5 py-3 mono text-mute text-[9px] tracking-[0.14em] uppercase border-b border-warmline bg-cream-deep min-w-[760px]`}>
          <div>Ref</div><div>Buyer</div><div>Item</div><div>Amount</div><div>Status</div><div className="text-right" />
        </div>

        {ordersLoading ? (
          [...Array(5)].map((_, i) => (
            <div key={i} className={`${ORDER_COLS} px-5 py-3.5 border-b border-warmline last:border-0 min-w-[760px]`}>
              <div className="h-3 w-16 bg-cream-deep rounded animate-pulse" />
              <div className="flex items-center gap-2.5"><div className="w-8 h-8 rounded-full bg-cream-deep animate-pulse" /><div className="h-3 w-24 bg-cream-deep rounded animate-pulse" /></div>
              <div className="h-3 w-40 bg-cream-deep rounded animate-pulse" />
              <div className="h-3 w-12 bg-cream-deep rounded animate-pulse" />
              <div className="h-5 w-20 bg-cream-deep rounded-full animate-pulse" />
              <div />
            </div>
          ))
        ) : recent.map((o) => {
          const s = orderStatus(o.state);
          const hero = o.id === heroId;
          return (
            <Link
              to={`/control-7f3a9b2c/orders/${o.id}`}
              key={o.id}
              className={`${ORDER_COLS} px-5 py-3.5 text-sm border-b border-warmline last:border-0 min-w-[760px] transition-all
                ${hero ? 'bg-ink text-cream shadow-luxe rounded-luxe relative z-[1]' : 'hover:bg-cream-deep/60'}`}
            >
              <div className={`mono text-[12px] ${hero ? 'text-gold-light' : 'text-ink'}`}>{o.reference}</div>
              <div className="flex items-center gap-2.5 min-w-0">
                <span className={`shrink-0 w-8 h-8 rounded-full grid place-items-center text-[11px] font-semibold display ${hero ? 'bg-cream/15 text-cream' : 'bg-sage text-cream'}`}>{initials(o.buyerName)}</span>
                <span className="font-medium truncate">{o.buyerName}</span>
              </div>
              <div className={`truncate ${hero ? 'text-cream/75' : 'text-mute'}`}>{o.productTitle}</div>
              <div className="display text-base">£{o.amountGBP.toFixed(2)}</div>
              <div>
                <span className={`pill text-[10px] ${hero ? 'bg-cream/15 text-cream' : s.pill}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />{s.label}
                </span>
              </div>
              <div className={`text-right ${hero ? 'text-cream/70' : 'text-mute-soft'}`}>›</div>
            </Link>
          );
        })}

        {!ordersLoading && !recent.length && (
          <div className="px-5 py-12 text-center text-mute text-sm">No orders yet.</div>
        )}

        {!ordersLoading && recent.length > 0 && (
          <div className="px-5 py-3 flex items-center justify-between text-[11px] text-mute border-t border-warmline">
            <span>Showing {recent.length} of {orders.length}</span>
            <Link to="/control-7f3a9b2c/orders" className="text-gold-dark hover:underline">All orders →</Link>
          </div>
        )}
      </div>

      {/* Manage */}
      <div className="flex items-baseline justify-between mt-9 mb-3.5">
        <h2 className="display text-2xl">Manage</h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
        {[
          ['👤', 'Users', 'Verify, suspend or reinstate accounts', '/control-7f3a9b2c/users'],
          ['🏷️', 'Listings & products', 'Take down or reinstate any listing', '/control-7f3a9b2c/listings'],
          ['📦', 'Orders & delivery', 'Track every order and its escrow state', '/control-7f3a9b2c/orders'],
          ['⚖️', 'Escrow & disputes', 'Release, refund and resolve disputes', '/control-7f3a9b2c/escrow'],
          ['📣', 'Complaints', 'Conduct complaints from both sides', '/control-7f3a9b2c/complaints'],
          ['✅', 'Verification queue', 'Approve or reject new listings', '/control-7f3a9b2c/verify'],
        ].map(([icon, title, desc, href]) => (
          <Link key={href} to={href} className="card p-4 flex items-start gap-3 hover:border-gold hover:-translate-y-0.5 hover:shadow-luxe transition-all">
            <span className="shrink-0 w-9 h-9 rounded-[11px] bg-gold-soft grid place-items-center text-lg">{icon}</span>
            <span className="min-w-0">
              <span className="block text-[13.5px] font-semibold">{title}</span>
              <span className="block text-[11.5px] text-mute mt-0.5 leading-snug">{desc}</span>
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function Tile({ label, value, to, accent, sub }: { label: string; value: string; to?: string; accent?: 'gold' | 'sage'; sub?: string }) {
  const hoverBorder = accent === 'sage' ? 'hover:border-sage' : 'hover:border-gold';
  const cls = `card-flat p-5 block transition-all hover:-translate-y-0.5 hover:shadow-luxe ${hoverBorder}`;
  const inner = (
    <>
      <div className="mono text-[9.5px] tracking-[0.2em] uppercase text-mute">{label}</div>
      <div className="display text-4xl mt-3 leading-none">{value}</div>
      {sub && <div className="text-[11px] text-mute mt-2.5">{sub}</div>}
    </>
  );
  return to ? <Link to={to} className={cls}>{inner}</Link> : <div className={cls}>{inner}</div>;
}

function Attn({ label, value, to, dot, on }: { label: string; value: string; to: string; dot: string; on: boolean }) {
  return (
    <Link to={to} className={`card-flat p-5 block transition-all hover:-translate-y-0.5 hover:shadow-luxe ${on ? 'border-gold' : ''}`}>
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${on ? dot : 'bg-warmline'}`} />
        <div className="mono text-[9.5px] tracking-[0.18em] uppercase text-ink-600">{label}</div>
      </div>
      <div className={`display text-4xl mt-3 leading-none ${on ? 'text-ink' : 'text-mute-soft'}`}>{value}</div>
    </Link>
  );
}
